// src/hooks/usePreferences.js (INFRA-172)
//
// Per-user preferences som följer mellan enheter — språk, theme, display-
// format, dashboard-layout, dismissed banners. Ersätter localStorage-baserad
// lagring med backend-first med localStorage som read-cache.
//
// Strategi:
//   1. Vid mount: om useCaseSyncedAtStart=false → pullar från backend en gång,
//      merger in i localStorage, markerar synced.
//   2. get(key): läser från localStorage-cache direkt (synkront).
//   3. set(key, value): uppdaterar localStorage direkt + fire-and-forget
//      PATCH /api/users/:id/preferences. Audit sker serverside.
//
// Failsafe: om backend är onåbart används localStorage-värden. Vid nästa
// lyckade sync uppdateras backend.
//
// Känd key-konvention:
//   app.language, app.theme, app.displayNameFormat, dashboard.layout,
//   licenseBanner.dismissed, (moduler kan definiera egna under egen prefix).

import { useCallback, useEffect, useState } from 'react';
import storage from '../services/StorageAdapter';
import sessionService from '../services/SessionService';
import { usersApi } from '../api';

const CACHE_KEY = 'rms_user_preferences_cache';
const SYNC_FLAG_KEY = 'rms_preferences_synced_at';
const PREFS_CHANGED_EVENT = 'rms_preferences_changed';

function loadCache() {
  return storage.getJSON(CACHE_KEY, {});
}

function saveCache(prefs) {
  storage.setJSON(CACHE_KEY, prefs);
  window.dispatchEvent(new Event(PREFS_CHANGED_EVENT));
}

function getCurrentUserId() {
  // Prefer backend-session sub; fallback localStorage-session username.
  const session = sessionService.getSession();
  if (session?.userId) return session.userId;
  if (session?.username) return session.username;
  return null;
}

// ------------------------------------------------------------------
// Module-level sync (körs vid App.js mount via ensurePreferencesSync).
// ------------------------------------------------------------------

let syncInFlight = null;

export async function ensurePreferencesSync({ force = false } = {}) {
  const userId = getCurrentUserId();
  if (!userId) return null;
  const lastSync = storage.getJSON(SYNC_FLAG_KEY, null);
  if (!force && lastSync && lastSync.userId === userId && Date.now() - lastSync.at < 60_000) {
    // Synced inom senaste minuten — skip
    return loadCache();
  }
  if (syncInFlight) return syncInFlight;
  syncInFlight = (async () => {
    try {
      const remote = await usersApi.getPreferences(userId);
      // Merge: remote vinner vid konflikt (det är den autoritativa källan)
      const local = loadCache();
      const merged = { ...local, ...remote };
      saveCache(merged);
      storage.setJSON(SYNC_FLAG_KEY, { userId, at: Date.now() });
      return merged;
    } catch (err) {
      // Backend onåbar — fortsätt med lokal cache
      return loadCache();
    } finally {
      syncInFlight = null;
    }
  })();
  return syncInFlight;
}

export function getPreference(key, fallback = null) {
  const cache = loadCache();
  return key in cache ? cache[key] : fallback;
}

export async function setPreference(key, value) {
  const cache = loadCache();
  cache[key] = value;
  saveCache(cache);

  const userId = getCurrentUserId();
  if (!userId) return value;
  try {
    await usersApi.setPreferences(userId, { [key]: value });
  } catch (_) {
    // Fire-and-forget — localStorage uppdaterat. Nästa sync fångar upp om
    // det är en verklig serverside-ändring som behöver lösas.
  }
  return value;
}

// ------------------------------------------------------------------
// React-hook
// ------------------------------------------------------------------

export function usePreferences() {
  const [prefs, setPrefsState] = useState(loadCache);

  useEffect(() => {
    const handleChanged = () => setPrefsState(loadCache());
    window.addEventListener(PREFS_CHANGED_EVENT, handleChanged);

    // Initial sync om nödvändigt
    ensurePreferencesSync().then(() => setPrefsState(loadCache()));

    return () => window.removeEventListener(PREFS_CHANGED_EVENT, handleChanged);
  }, []);

  const get = useCallback((key, fallback = null) => {
    return key in prefs ? prefs[key] : fallback;
  }, [prefs]);

  const set = useCallback(async (key, value) => {
    await setPreference(key, value);
  }, []);

  return { prefs, get, set };
}

export default usePreferences;
